import { sinkListItem, liftListItem } from "prosemirror-schema-list";
import {
    autoJoin,
    chainCommands,
    wrapIn,
    joinBackward,
    lift,
    joinTextblockBackward,
} from "prosemirror-commands";
import { NodeType, ResolvedPos, Schema } from "prosemirror-model";
import { EditorView } from "prosemirror-view";
import {
    Command,
    EditorState,
    NodeSelection,
    TextSelection,
} from "prosemirror-state";

const getNearestListNode = (currentState: EditorState) => {
    const { $from, to } = currentState.selection;
    const same = $from.sharedDepth(to);
    for (let depth = same; depth !== 0; depth -= 1) {
        const pos = $from.before(depth);
        const nodeSelection = NodeSelection.create(currentState.doc, pos);
        if (
            nodeSelection.node.type.name === "bullet_list" ||
            nodeSelection.node.type.name === "ordered_list"
        ) {
            return nodeSelection;
        }
    }
    return null;
};

const convertToListType: (listType: NodeType) => Command =
    (listType: NodeType) => (state, dispatch, view) => {
        const nearestListSelection = getNearestListNode(state);
        if (
            !nearestListSelection ||
            nearestListSelection.node.type === listType
        ) {
            return false;
        }
        if (dispatch) {
            const tr = view.state.tr.setNodeMarkup(
                nearestListSelection.from,
                listType
            );
            dispatch(tr);
        }
        return true;
    };

const indent = (schema: Schema) =>
    autoJoin(
        chainCommands(
            sinkListItem(schema.nodes.list_item),
            wrapIn(schema.nodes.blockquote),
            () => true
        ),
        (before, after) => {
            return (
                ["ordered_list", "bullet_list"].includes(before.type.name) &&
                ["ordered_list", "bullet_list"].includes(after.type.name)
            );
        }
    );

const joinBackwardUnlessAtDocStart: Command = (state, dispatch, view) => {
    if (state.selection.$head.depth <= 1) {
        // We are at the left margin, so performing a backward join here
        // would move the cursor to the previous block
        return false;
    }
    return joinBackward(state, dispatch, view);
};

const unindent = (schema: Schema) =>
    chainCommands(
        liftListItem(schema.nodes.list_item),
        joinBackwardUnlessAtDocStart,
        lift
    );

function atBlockStart(
    state: EditorState,
    view?: EditorView
): ResolvedPos | null {
    const { $cursor } = state.selection as TextSelection;
    if (
        !$cursor ||
        (view
            ? !view.endOfTextblock("backward", state)
            : $cursor.parentOffset > 0)
    )
        return null;
    return $cursor;
}

const liftListItemIfAtStart: (listItemType: NodeType) => Command =
    (listItemType) => (state, dispatch, view) => {
        if (!atBlockStart(state, view)) {
            return false;
        }
        // Check that we are at the start
        return liftListItem(listItemType)(state, dispatch, view);
    };

const joinTopLevelWithPreviousListItem: Command = (state, dispatch, view) => {
    if (!atBlockStart(state, view)) {
        return false;
    }
    if (state.selection.$from.depth !== 1) {
        return false;
    }
    return joinTextblockBackward(state, dispatch, view);
};

export {
    convertToListType,
    indent,
    unindent,
    getNearestListNode,
    liftListItemIfAtStart,
    joinTopLevelWithPreviousListItem,
};
